import { useEffect, useMemo, useState } from "react";
import { NMAxiosInst } from "@neumetric/blueprint-lib/store";
import { DateTimeUtil } from "@neumetric/blueprint-lib/utility";
import {
  Col,
  NMStatsTable,
} from "@neumetric/blueprint-lib/components";
import { ExportSection } from "../ExportSection";

const MODULE_KEY = "Auditor_Audit_Status";

const Columns = [
  {
    title: "",
    attribute: "title",
    width: "25%",
    cellClassName: "ellipsis-text",
  },
  {
    title: "Start Date",
    attribute: "start_date",
    align: "left",
    width: "10%",
    classes: { color: "text-filled" },
  },
  {
    title: "End Date",
    attribute: "end_date",
    align: "left",
    width: "10%",
    classes: { color: "text-filled" },
  },
  {
    title: "Status",
    attribute: "status",
    align: "left",
    typeRepresentation: "DOT",
  },
  {
    title: "Controls",
    attribute: "controls",
    overallFlag: true,
    highlight: { header: true, cell: true },
  },
  { title: "Compliant", attribute: "compliant", id: "CLOSED" },
  { title: "Gap", attribute: "gap", id: "GAP" },
  { title: "Exemption", attribute: "exemption", id: "INVALID" },
  { title: "Pending", attribute: "pending", id: "REDUNDANT" },
  { title: "Score", attribute: "score", align: "center-right" },
];

const Divider = () => (
  <div style={{ margin: "10px 0" }}>
    <hr className="nm-hr nm-hr--center" />
  </div>
);

export function AuditorAssessmentStats(props) {
  const { handleSaveAudits, dateRange } = props;
  const [rows, setRows] = useState(null);
  const effectiveRangeId =
    typeof dateRange === "string" ? dateRange : dateRange?.id || "last_365_days";

  const getScoreContent = (score) => (
    <span className="ff-exo2 fs-15 text-heading">
      {score} <span className="fs-11 line-15 text-filled">/ 10</span>
    </span>
  );

  const getStatus = (status) => {
    let statusId = status?.toUpperCase() ?? "";
    let statusLabel = status ?? "";
    switch (statusId) {
      case "REVISION":
        statusId = "HOLD";
        break;
      case "EVALUATED":
      case "EVALUATING":
        statusId = "REVIEW";
        break;
      case "SUBMITTED":
        statusId = "STARTED";
        break;
      case "CREATING":
        statusId = "PENDING";
        statusLabel = "Pending";
        break;
    }
    if (
      statusLabel.toUpperCase() !== "PENDING" &&
      statusLabel.endsWith("ing")
    ) {
      statusLabel = statusLabel.slice(0, -3) + "ed";
    }
    return { id: statusId, label: statusLabel };
  };

  const fetchData = async (isCurrentRequest) => {
    const payload = { date_range_type: effectiveRangeId };
    let res = { audit_stat: [] };

    try {
      res = await NMAxiosInst.post("/centrals/dashboard", payload);
    } catch (error) {
      console.error("Error fetching auditor stats:", error);
    }

    const list = Array.isArray(res?.audit_stat) ? res.audit_stat : [];
    const normalizedRaw = [];
    const tableRows = [];

    list.forEach((r, i) => {
      const normalizedItem = {
        key: r?.id ?? `${r?.title ?? "AUDIT"}-${i}`,
        title: r?.title ?? `Audit ${i + 1}`,
        start_date: r?.start_date ?? null,
        end_date: r?.end_date ?? null,
        status: (r?.status ?? "").toString(),
        controls: Number(r?.controls ?? 0),
        pending: Number(r?.pending ?? 0),
        compliant: Number(r?.compliant ?? 0),
        gap: Number(r?.gap ?? 0),
        exemption: Number(r?.exemption ?? 0),
        score: Number(r?.score ?? 0),
        id: r?.asmt_id ?? null,
      };
      normalizedRaw.push(normalizedItem);

      const t = {
        title: normalizedItem.title,
        start_date: normalizedItem.start_date
          ? new DateTimeUtil(normalizedItem.start_date).toDateFullYearString()
          : "-",
        end_date: normalizedItem.end_date
          ? new DateTimeUtil(normalizedItem.end_date).toDateFullYearString()
          : "-",
        status: getStatus(normalizedItem.status),
        controls: normalizedItem.controls,
        pending: normalizedItem.pending,
        compliant: normalizedItem.compliant,
        gap: normalizedItem.gap,
        exemption: normalizedItem.exemption,
        score: getScoreContent(normalizedItem.score),
      };

      Object.keys(t).forEach((k) => {
        const v = t[k];
        if (v === 0 || v === "0" || v === "") t[k] = "-";
      });

      tableRows.push(t);
    });

    if (isCurrentRequest()) {
      handleSaveAudits?.(normalizedRaw);
      setRows(tableRows);
    }
  };

  useEffect(() => {
    let cancelled = false;
    fetchData(() => !cancelled);
    return () => {
      cancelled = true;
    };
  }, [effectiveRangeId, handleSaveAudits]);

  return (
    <div className="o-auto" style={{ padding: "20px 40px 0 40px" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          padding: "0",
        }}
      >
        <ExportSection
          moduleKey={MODULE_KEY}
          index={1}
          exportType="table"
          exportData={{
            title: "Audit - Department-wise",
            columns: Columns,
            rows,
          }}
        >
          <Col className="w-100" style={{ maxWidth: "1392px" }}>
            <NMStatsTable
              title="Audit"
              subTitle="Framework-wise"
              columns={Columns}
              dataSource={rows}
              showDateInHeader={true}
              initialRowLength={6}
              getCellTypeRepresentation={(cellInfo) => {
                if (
                  cellInfo.rowId === -1 &&
                  ["pending", "compliant", "gap", "exemption"].includes(
                    cellInfo.columnId
                  )
                )
                  return "DOT";
                if (cellInfo.columnId === "status") return "DOT";
                return null;
              }}
            />
          </Col>
        </ExportSection>
        <Divider />
      </div>
    </div>
  );
}
